import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Printer } from 'lucide-react';
import api from '../../utils/api';
import { isNativePlatform } from '../../utils/device';

function countStudents(params) {
  if (!params) return 0;
  if (params.student_ids) {
    return String(params.student_ids).split(',').filter(Boolean).length;
  }
  return params.student_id ? 1 : 0;
}

export default function DocumentPreviewModal({
  open,
  onOpenChange,
  documentType,
  previewPath,
  params,
  title = 'Document Preview',
  wide = false,
}) {
  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState('');
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null);

  const endpoint = previewPath || `/reports/${documentType}/preview`;
  const studentCount = useMemo(() => countStudents(params), [params]);
  const canFetch = Boolean(
    open && params && (params.student_id || params.student_ids),
  );

  useEffect(() => {
    if (!canFetch) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setHtml('');
    setMeta(null);
    api.get(endpoint, { params })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || {};
        setHtml(data.html || '');
        setMeta({
          rendered: data.rendered,
          requested: data.requested,
          skipped: data.skipped,
        });
        if (!data.html) setError('Preview was empty');
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.message || 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [canFetch, endpoint, params]);

  const handlePrint = () => {
    if (isNativePlatform()) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    w.print();
  };

  const metaLine = meta?.requested > 1
    ? `${meta.rendered ?? studentCount} of ${meta.requested} student cards`
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${wide ? 'max-w-6xl' : 'max-w-4xl'} w-[95vw] h-[90vh] flex flex-col`}>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <DialogTitle>{title}</DialogTitle>
            {metaLine && (
              <p className="text-xs text-muted-foreground mt-1">{metaLine}</p>
            )}
            {meta?.skipped?.length > 0 && (
              <p className="text-xs text-amber-600 mt-0.5">
                {meta.skipped.length} skipped (missing data)
              </p>
            )}
          </div>
          {!isNativePlatform() && (
            <Button type="button" variant="outline" size="sm" disabled={!html} onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />Print
            </Button>
          )}
        </DialogHeader>
        <div className="flex-1 min-h-0 rounded-lg border overflow-auto bg-white">
          {loading ? (
            <div className="h-full min-h-[200px] flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              {studentCount > 1 ? `Loading ${studentCount} cards…` : 'Loading…'}
            </div>
          ) : error ? (
            <div className="h-full min-h-[200px] flex items-center justify-center text-red-500 px-4 text-center">{error}</div>
          ) : (
            <iframe
              title="report-preview"
              className="w-full min-h-full bg-white border-0"
              style={{ minHeight: '70vh' }}
              sandbox="allow-same-origin allow-modals"
              srcDoc={html}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
